import { Box, Stack, Text } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import type { CodeReviewAction, TransactionCode } from '../../interface/transactionCodes';
import {
  INK_MUTED,
  ROW_GAP,
  SECTION_LABEL_SIZE,
  SECTION_LABEL_SPACING,
  SECTION_LABEL_WEIGHT
} from '../../styles/tokens';
import { CodeRow } from './CodeRow';

export type CodeSectionProps = {
  readonly label: 'Procedures' | 'Modifiers' | 'Diagnoses';
  readonly sectionKey: 'procedures' | 'modifiers' | 'diagnoses';
  readonly codes: readonly TransactionCode[];
  readonly addButton?: React.ReactNode;
  readonly onReorder?: (orderedIds: readonly string[]) => Promise<void>;
  readonly expandedId?: string;
  readonly onToggleCode?: (id: string) => void;
  readonly onCodeAction?: (code: TransactionCode, action: CodeReviewAction) => void;
  readonly hint?: { codeId: string; action: CodeReviewAction };
};

export const SectionLabel: React.FC<{ readonly children: React.ReactNode }> = ({ children }) => (
  <Text
    as="span"
    fontSize={SECTION_LABEL_SIZE}
    fontWeight={SECTION_LABEL_WEIGHT}
    letterSpacing={SECTION_LABEL_SPACING}
    textTransform="uppercase"
    color={INK_MUTED}
  >
    {children}
  </Text>
);

export const CodeSection: React.FC<CodeSectionProps> = ({ label, sectionKey, codes, addButton, onReorder, expandedId, onToggleCode, onCodeAction, hint }) => {
  const reorderable = sectionKey !== 'procedures';
  const grabber = !reorderable ? 'none' : onReorder && codes.length > 1 ? 'active' : 'inert';
  const [order, setOrder] = useState<readonly string[] | undefined>(undefined);
  const [dragging, setDragging] = useState<string | undefined>(undefined);
  const [target, setTarget] = useState<{ id: string; side: 'before' | 'after' } | undefined>(undefined);
  const [error, setError] = useState(false);

  const [pending, setPending] = useState(false);
  const generation = useRef(0);
  const signature = codes.map((code) => code.id).join('\n');

  useEffect(() => {
    generation.current += 1;
    setOrder(undefined);
    setError(false);
  }, [signature]);

  const byId = new Map(codes.map((code) => [code.id, code]));
  const ids = order ?? codes.map((code) => code.id);
  const shown = ids.map((id) => byId.get(id)).filter((code): code is TransactionCode => code !== undefined);

  const sideFor = (row: HTMLElement, clientY: number): 'before' | 'after' => {
    const rect = row.getBoundingClientRect();
    return clientY < rect.top + rect.height / 2 ? 'before' : 'after';
  };

  const reorder = (draggedId: string, targetId: string, side: 'before' | 'after'): readonly string[] => {
    const without = ids.filter((id) => id !== draggedId);
    const at = without.indexOf(targetId);
    const insertAt = side === 'before' ? at : at + 1;
    return [...without.slice(0, insertAt), draggedId, ...without.slice(insertAt)];
  };

  const onDrop = async (targetId: string, event: React.DragEvent) => {
    const draggedId = dragging ?? event.dataTransfer?.getData('text/plain');
    if (!draggedId || !ids.includes(draggedId)) {
      setTarget(undefined);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setTarget(undefined);
    setDragging(undefined);
    if (!onReorder || pending || draggedId === targetId) return;
    const next = reorder(draggedId, targetId, sideFor(event.currentTarget as HTMLElement, event.clientY));
    if (next.every((id, i) => id === ids[i])) return;
    const previous = ids;
    const startedAt = generation.current;
    setOrder(next);
    setError(false);
    setPending(true);
    try {
      await onReorder(next);
    } catch {
      setOrder(generation.current === startedAt ? previous : undefined);
      setError(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <Box role="group" aria-label={label} data-section={sectionKey}>
      {sectionKey !== 'procedures' && <SectionLabel>{label}</SectionLabel>}
      <Stack spacing={ROW_GAP} mt={sectionKey === 'procedures' ? 0 : '4px'}>
        {shown.map((code) => (
          <CodeRow
            key={code.id}
            code={code}
            grabber={grabber}
            dropIndicator={target?.id === code.id ? target.side : undefined}
            expanded={expandedId === code.id}
            onToggle={onToggleCode && (() => onToggleCode(code.id))}
            onAction={onCodeAction && ((action) => onCodeAction(code, action))}
            hint={hint?.codeId === code.id ? hint.action : undefined}
            onGrabberDragStart={(event) => {
              event.dataTransfer?.setData('text/plain', code.id);
              if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
              setDragging(code.id);
            }}
            onDragOver={(event) => {
              if (!dragging || dragging === code.id) return;
              event.preventDefault();
              event.stopPropagation();
              setTarget({ id: code.id, side: sideFor(event.currentTarget as HTMLElement, event.clientY) });
            }}
            onDrop={(event) => void onDrop(code.id, event)}
            onDragEnd={() => {
              setDragging(undefined);
              setTarget(undefined);
            }}
          />
        ))}
        {error && (
          <Text role="alert" fontSize="11px" color="var(--color-alert-500, #e50000)">
            Couldn't reorder. Try again.
          </Text>
        )}
        {addButton && <Box>{addButton}</Box>}
      </Stack>
    </Box>
  );
};
